import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { type UseAutocompleteReturnValue } from '@mui/material/useAutocomplete';
import { useRef, type Ref } from 'react';

import { RequiredChip } from '@/components/Chips/RequiredChip';

import type { DropdownItemBase } from '../autocomplete.types';

import { autocompleteFilterStyles as sx } from './AutocompleteInput.styles';

type AutoReturn<T extends DropdownItemBase> = UseAutocompleteReturnValue<
	T,
	false
>;

type Props<T extends DropdownItemBase> = {
	placeholder: string;
	setAnchorEl: AutoReturn<T>['setAnchorEl'];
	getInputProps: AutoReturn<T>['getInputProps'];
	popupOpen: AutoReturn<T>['popupOpen'];
	name?: string;
	disabled?: boolean;
	indicateError?: boolean;
	isDirty?: boolean;
	isRequired?: boolean;
};

export const AutocompleteInput = <T extends DropdownItemBase>({
	getInputProps,
	popupOpen,
	setAnchorEl,
	placeholder,
	disabled,
	indicateError,
	isDirty,
	name,
	isRequired = false
}: Props<T>) => {
	const inputRef = useRef<HTMLInputElement | null>(null);

	const handleAdornmentClick = () => {
		const input = inputRef.current;
		if (!input) return;
		input.focus();
		input.dispatchEvent(
			new KeyboardEvent('keydown', {
				bubbles: true,
				key: popupOpen ? 'Escape' : 'ArrowDown'
			})
		);
	};

	return (
		<TextField
			ref={setAnchorEl as Ref<HTMLDivElement>}
			name={name}
			inputRef={inputRef}
			placeholder={placeholder}
			fullWidth
			disabled={disabled}
			error={indicateError ? true : false}
			sx={() => sx.inputContainer(!!indicateError, !!isDirty)}
			slotProps={{
				htmlInput: {
					...getInputProps(),
					sx: sx.input
				},
				input: {
					endAdornment: (
						<InputAdornment
							position='end'
							onClick={handleAdornmentClick}
							sx={sx.dropdownIcon}
						>
							<>
								<Box sx={sx.badgeWrap}>
									{isRequired ? (
										<RequiredChip indicateError={indicateError} />
									) : null}
								</Box>
								{popupOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
							</>
						</InputAdornment>
					)
				}
			}}
		/>
	);
};
